/* eslint-disable @typescript-eslint/no-unused-vars */
import { ComponentIds, ConnectionUtils, ReplaceUtils, ValidationResultFail } from '@inter-ikea-core/core';
import { PlacementResult } from '@inter-ikea-core/core';
import { MultiHandleComponent } from '..';
import { autoAdaptChildrenAfterReplace } from '../utils/autoAdapt';
export class BaseProductApi {
    constructor(coreServices, placementService, reportErrorToSentry) {
        this.coreServices = coreServices;
        this.placementService = placementService;
        this.ipexViewer = {};
        this.reportErrorToSentry = reportErrorToSentry;
    }
    setIpexViewer(ipexViewer) {
        this.ipexViewer = ipexViewer;
    }
    add(productId, position) {
        const entity = this.coreServices.productManager.createEntity(productId);
        if (!entity) {
            return new PlacementResult([], [new ValidationResultFail('Failed to create entity', productId)], null);
        }
        const viewer = this.coreServices.coreModules.viewer();
        const worldPosition = position ? viewer?.raycast(position.x, position.y) : null;
        return worldPosition
            ? this.placementService.snapToPosition(entity, worldPosition, false)
            : this.placementService.snapToScene(entity);
    }
    addToTargets(productId, targets) {
        const entity = this.coreServices.productManager.createEntity(productId);
        return entity
            ? this.placementService.snapToTargets(entity, targets)
            : new PlacementResult([], [new ValidationResultFail('Failed to create entity', productId)], null);
    }
    addCluster(productIds, position) {
        return this.add(productIds[0], position);
    }
    addToTarget(productId, target, sortAndSelectStrategy) {
        const entity = this.coreServices.productManager.createEntity(productId);
        return entity
            ? this.placementService.snapToTarget(entity, target, sortAndSelectStrategy)
            : new PlacementResult([], [new ValidationResultFail('Failed to create entity', productId)], null);
    }
    snapToConnector(entity, productConnectorKey, target, targetConnectorKey) {
        return this.placementService.snapToConnector(entity, productConnectorKey, target, targetConnectorKey);
    }
    autoFillDoors(targets, oldDoors, products) {
        throw new Error('Method not implemented.');
    }
    autoFillHandles(handleId, oldHandles, fronts) {
        oldHandles.forEach((handle) => {
            if (!handle.isDisposed) {
                ConnectionUtils.disconnectAndDisposeEntityAndHierarchy(handle);
            }
        });
        return fronts.flatMap(front => {
            const multiHandle = front.getComponent(MultiHandleComponent.id);
            return multiHandle
                ? new Array(multiHandle.nbrOfHandles).fill(handleId).map(handleId => {
                    return this.addToTarget(handleId, front);
                })
                : this.addToTarget(handleId, front);
        });
    }
    addToConnector(productId, target, guestKey, hostKey) {
        const entity = this.coreServices.productManager.createEntity(productId);
        if (!entity) {
            return new PlacementResult([], [new ValidationResultFail('Failed to create entity', productId)], null);
        }
        return this.placementService.snapToConnector(entity, guestKey, target, hostKey);
    }
    autoFillInnerLights(productId, targets, oldLights) {
        throw new Error('Method not implemented.');
    }
    autoFillOuterLights(productId, frames) {
        throw new Error('Method not implemented.');
    }
    addOuterLightsOnFrame(productId, frame, double, removeOldLights) {
        throw new Error('Method not implemented.');
    }
    replace(entityId, hostProductId, fastReplace = false, allowedMovementOfChildrenInMilliMeter = Number.MAX_VALUE) {
        const selectedEntity = this.coreServices.sceneManager.findEntity(entityId);
        const newEntity = this.coreServices.productManager.createEntity(hostProductId);
        if (!selectedEntity || !newEntity) {
            return null;
        }
        const replacePlacementResult = fastReplace
            ? ReplaceUtils.fastReplaceEntity(selectedEntity, newEntity, this.coreServices)
            : ReplaceUtils.replaceEntity(selectedEntity, newEntity, allowedMovementOfChildrenInMilliMeter, this.coreServices);
        if (replacePlacementResult.replacedEntityResult.valid) {
            selectedEntity.dispose();
        }
        return replacePlacementResult;
    }
    replaceEntityWithHierarchy(entityId, hostProductId, replaceInvalidEntities, allowedMovementOfChildrenInMilliMeter) {
        const replacePlacementResult = this.replace(entityId, hostProductId, false, allowedMovementOfChildrenInMilliMeter);
        const newEntity = replacePlacementResult?.replacedEntityResult.placedEntity || null;
        if (replacePlacementResult && newEntity) {
            // If we want to replace invalid child entities we will try to autoAdapt them to the new entity
            // In some cases e.g when changing the height of a frame, we want to remove everything that doesn't fit the frame
            const childrenWithNoFuture = replaceInvalidEntities
                ? autoAdaptChildrenAfterReplace(newEntity, replacePlacementResult, this.placementService)
                : replacePlacementResult.childrenResult
                    .filter(childPlacementResult => !childPlacementResult.valid)
                    .map(childPlacementResult => childPlacementResult);
            const entitiesToRemove = childrenWithNoFuture.filter(c => !c.valid).map(c => c.placedEntity);
            entitiesToRemove.forEach(e => {
                if (e)
                    ConnectionUtils.disconnectAndDisposeEntityAndHierarchy(e);
            });
        }
        return newEntity;
    }
    removeAndAdd(entityId, hostProductId) {
        const selectedEntity = this.coreServices.sceneManager.findEntity(entityId);
        const newEntity = this.coreServices.productManager.createEntity(hostProductId);
        if (!selectedEntity || !newEntity) {
            return null;
        }
        const selectedEntityPosition = selectedEntity.getComponent(ComponentIds.WORLD_TRANSFORM_COMPONENT_ID).transform.position;
        if (!selectedEntity.isDisposed) {
            ConnectionUtils.disconnectAndDisposeEntityAndHierarchy(selectedEntity);
        }
        const placementResult = this.placementService.snapToPosition(newEntity, selectedEntityPosition, true);
        return placementResult;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    performAction(action, params) {
        throw new Error('Method not implemented.');
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isGlassDoor(productId, products) {
        return !!products.find(p => p.coreProductId === productId)?.meta?.design?.includes('glass');
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getTops(products) {
        throw new Error('Method should be implemented in range specfic product api');
    }
    getDefaultLeg() {
        throw new Error('Method should be implemented in range specfic product api');
    }
    getDefaultHighLegForAutoFill() {
        throw new Error('Method should be implemented in range specfic product api for auto fill');
    }
}
//# sourceMappingURL=BaseProductApi.js.map